import React, { useState } from "react";
import { Mail, Linkedin, Send } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const CONTACT_EMAIL = "naari.livingtech@gmail.com";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", linkedin: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitContact({
        name: form.name,
        email: form.email,
        message: form.message,
        linkedin: form.linkedin || null,
      });
      toast.success(res.message || "Message sent!");
      setForm({ name: "", email: "", linkedin: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      <section className="relative overflow-hidden py-12 md:py-20">
        <div className="mesh-bg">
          <div className="mesh-blob" style={{ width: 440, height: 440, background: "#F28C82", top: -80, right: -40 }} />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-6 md:px-12">
          <div className="grid items-stretch gap-12 lg:grid-cols-2">
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">Contact</p>
              <h1 className="mt-6 font-heading text-4xl font-light tracking-tighter text-[#4B2E5A] md:text-5xl">Let's talk</h1>
              <p className="mt-6 text-lg leading-relaxed text-[#4B2E5A]/70">
                Questions, partnerships, press or just saying hello — we'd love to hear from you.
              </p>
              <img
                src="/assets/contact-illustration.png"
                alt="Get in touch with Naari"
                className="mt-8 w-full rounded-[24px]"
              />
              <div className="mt-8 space-y-4">
                <a href={`mailto:${CONTACT_EMAIL}`} className="glass flex items-center gap-4 rounded-2xl p-5 transition-transform hover:-translate-y-0.5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F28C82]/15">
                    <Mail className="h-5 w-5 text-[#F28C82]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#4B2E5A]/50">Email</p>
                    <p className="font-medium text-[#4B2E5A]">{CONTACT_EMAIL}</p>
                  </div>
                </a>
                <a href="https://www.linkedin.com/in/ambalika-smiti/" target="_blank" rel="noopener noreferrer" className="glass flex items-center gap-4 rounded-2xl p-5 transition-transform hover:-translate-y-0.5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#B8E3D9]/40">
                    <Linkedin className="h-5 w-5 text-[#4B2E5A]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#4B2E5A]/50">LinkedIn</p>
                    <p className="font-medium text-[#4B2E5A]">Connect with us</p>
                  </div>
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="flex">
              <form onSubmit={submit} data-testid="contact-form" className="glass flex h-full w-full flex-col gap-4 rounded-[24px] p-8">
                <Input data-testid="contact-name" required placeholder="Your name" value={form.name} onChange={update("name")} className="rounded-xl border-[#4B2E5A]/15 bg-white px-4 py-6 text-[#4B2E5A]" />
                <Input data-testid="contact-email" type="email" required placeholder="you@email.com" value={form.email} onChange={update("email")} className="rounded-xl border-[#4B2E5A]/15 bg-white px-4 py-6 text-[#4B2E5A]" />
                <Input data-testid="contact-linkedin" placeholder="LinkedIn (optional)" value={form.linkedin} onChange={update("linkedin")} className="rounded-xl border-[#4B2E5A]/15 bg-white px-4 py-6 text-[#4B2E5A]" />
                <Textarea data-testid="contact-message" required placeholder="Your message" rows={5} value={form.message} onChange={update("message")} className="min-h-[160px] flex-1 resize-none rounded-xl border-[#4B2E5A]/15 bg-white px-4 py-3 text-[#4B2E5A]" />
                <button data-testid="contact-submit" type="submit" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4B2E5A] py-4 text-sm font-medium text-[#FCFAF7] hover:bg-[#4B2E5A]/90">
                  {loading ? "Sending..." : "Send message"} <Send className="h-4 w-4" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
